import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import { platformStorage } from '@/lib/platform-storage'
import { notifyFromStore } from '@/hooks/use-notifications'

// Module-level translations cache — populated by TimerRuntime via setPomodoroTranslations().
let _pt = {
  focus_notif_title: 'Focus session complete!',
  focus_notif_message: 'Great work! Time for a well-deserved break.',
  break_notif_title: 'Break is over!',
  break_notif_message: 'Break is over — ready to get back to work?',
}

export function setPomodoroTranslations(t: typeof _pt): void {
  _pt = t
}

// ── Types ──────────────────────────────────────────────────────────────────────

export type PomodoroPhase = 'focus' | 'short_break' | 'long_break'

export interface PomodoroSettings {
  focusDuration: number   // seconds
  shortBreak: number      // seconds
  longBreak: number       // seconds
  pomodorosBeforeLongBreak: number
}

export interface PomodoroTimerState {
  // Timer identity
  selectedTaskId: string
  phase: PomodoroPhase
  timeRemaining: number        // seconds
  isRunning: boolean
  showPhaseComplete: boolean

  // Cycle tracking
  completedPomodoros: number         // within current cycle (resets after long break)
  totalPomodorosCompleted: number    // lifetime for this session

  // Settings
  settings: PomodoroSettings

  // Focus-session tracking (for time entries)
  focusStartTime: string | null      // ISO string — serialisable for devtools
  runEndsAt: number | null           // epoch ms when current phase ends

  // Computed helpers
  readonly isActive: boolean
}

// ── Defaults ───────────────────────────────────────────────────────────────────

const DEFAULT_SETTINGS: PomodoroSettings = {
  focusDuration: 25 * 60,
  shortBreak: 5 * 60,
  longBreak: 15 * 60,
  pomodorosBeforeLongBreak: 4,
}

// ── Store interface ────────────────────────────────────────────────────────────

interface PomodoroTimerStore extends PomodoroTimerState {
  // Actions
  setSelectedTaskId: (taskId: string) => void
  startPause: () => void
  tick: () => void
  syncWithCurrentTime: () => void
  advancePhase: () => void
  skipPhase: () => void
  reset: () => void
  applySettings: (settings: PomodoroSettings) => void
  clearTask: () => void
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function phaseDuration(phase: PomodoroPhase, settings: PomodoroSettings): number {
  switch (phase) {
    case 'focus':
      return settings.focusDuration
    case 'short_break':
      return settings.shortBreak
    case 'long_break':
      return settings.longBreak
  }
}

// ── Store ──────────────────────────────────────────────────────────────────────

export const usePomodoroTimerStore = create<PomodoroTimerStore>()(
  persist(
    devtools(
      (set, get) => ({
      // ── Initial state ──────────────────────────────────────────────────
      selectedTaskId: '',
      phase: 'focus',
      timeRemaining: DEFAULT_SETTINGS.focusDuration,
      isRunning: false,
      showPhaseComplete: false,
      completedPomodoros: 0,
      totalPomodorosCompleted: 0,
      settings: { ...DEFAULT_SETTINGS },
      focusStartTime: null,
      runEndsAt: null,

      get isActive() {
        const state = get()
        return (
          state.isRunning ||
          state.showPhaseComplete ||
          state.timeRemaining < phaseDuration(state.phase, state.settings)
        )
      },

      // ── Actions ────────────────────────────────────────────────────────

      setSelectedTaskId: (taskId: string) => {
        const { isRunning } = get()
        if (isRunning) return // prevent changing task while running
        set({ selectedTaskId: taskId }, false, 'pomodoro/setSelectedTaskId')
      },

      startPause: () => {
        const { showPhaseComplete, isRunning, phase, focusStartTime, timeRemaining } = get()
        if (showPhaseComplete) return

        if (isRunning) {
          get().syncWithCurrentTime()
          if (get().showPhaseComplete) return
          set({ isRunning: false, runEndsAt: null }, false, 'pomodoro/pause')
        } else {
          if (timeRemaining <= 0) return
          const updates: Partial<PomodoroTimerStore> = {
            isRunning: true,
            runEndsAt: Date.now() + timeRemaining * 1000,
          }
          if (phase === 'focus' && !focusStartTime) {
            updates.focusStartTime = new Date().toISOString()
          }
          set(updates, false, 'pomodoro/start')
        }
      },

      tick: () => {
        get().syncWithCurrentTime()
      },

      syncWithCurrentTime: () => {
        const { isRunning, runEndsAt, timeRemaining } = get()
        if (!isRunning || !runEndsAt) return

        const nextRemaining = Math.max(0, Math.ceil((runEndsAt - Date.now()) / 1000))

        if (nextRemaining <= 0) {
          const { phase, selectedTaskId } = get()
          set(
            { timeRemaining: 0, isRunning: false, showPhaseComplete: true, runEndsAt: null },
            false,
            'pomodoro/phaseComplete',
          )

          // Send notification
          const isFocus = phase === 'focus'
          notifyFromStore({
            type: isFocus ? 'pomodoro_focus_end' : 'pomodoro_break_end',
            title: isFocus ? _pt.focus_notif_title : _pt.break_notif_title,
            message: isFocus ? _pt.focus_notif_message : _pt.break_notif_message,
            taskId: selectedTaskId || undefined,
            tag: `pomodoro-${phase}-${Date.now()}`,
          })
          return
        }

        if (nextRemaining !== timeRemaining) {
          set({ timeRemaining: nextRemaining }, false, 'pomodoro/tick')
        }
      },

      advancePhase: () => {
        const { phase, completedPomodoros, settings } = get()

        if (phase === 'focus') {
          const newCount = completedPomodoros + 1
          const isLongBreak = newCount % settings.pomodorosBeforeLongBreak === 0
          set({
            showPhaseComplete: false,
            completedPomodoros: newCount,
            totalPomodorosCompleted: get().totalPomodorosCompleted + 1,
            phase: isLongBreak ? 'long_break' : 'short_break',
            timeRemaining: isLongBreak ? settings.longBreak : settings.shortBreak,
            focusStartTime: null,
            runEndsAt: null,
          }, false, 'pomodoro/advanceToBreak')
        } else {
          // Break → Focus
          set({
            showPhaseComplete: false,
            phase: 'focus',
            timeRemaining: settings.focusDuration,
            focusStartTime: new Date().toISOString(),
            runEndsAt: null,
          }, false, 'pomodoro/advanceToFocus')
        }
      },

      skipPhase: () => {
        set({ isRunning: false, runEndsAt: null }, false, 'pomodoro/skipPause')
        get().advancePhase()
      },

      reset: () => {
        const { settings } = get()
        set({
          isRunning: false,
          showPhaseComplete: false,
          phase: 'focus',
          timeRemaining: settings.focusDuration,
          completedPomodoros: 0,
          totalPomodorosCompleted: 0,
          focusStartTime: null,
          runEndsAt: null,
        }, false, 'pomodoro/reset')
      },

      applySettings: (newSettings: PomodoroSettings) => {
        set({
          settings: newSettings,
          isRunning: false,
          showPhaseComplete: false,
          phase: 'focus',
          timeRemaining: newSettings.focusDuration,
          completedPomodoros: 0,
          totalPomodorosCompleted: 0,
          focusStartTime: null,
          runEndsAt: null,
        }, false, 'pomodoro/applySettings')
      },

      clearTask: () => {
        set({ selectedTaskId: '' }, false, 'pomodoro/clearTask')
      },

      // ── Internal ───────────────────────────────────────────────────────
      }),
      { name: 'PomodoroTimerStore' },
    ),
    {
      name: 'pomodoro-timer-store',
      // Named rather than defaulted: zustand falls back to `localStorage`,
      // which does not exist on a phone. Metro resolves the `.native`
      // sibling to AsyncStorage so this store file runs on both clients.
      storage: createJSONStorage(() => platformStorage),
      partialize: (state) => ({
        selectedTaskId: state.selectedTaskId,
        phase: state.phase,
        timeRemaining: state.timeRemaining,
        isRunning: state.isRunning,
        showPhaseComplete: state.showPhaseComplete,
        completedPomodoros: state.completedPomodoros,
        totalPomodorosCompleted: state.totalPomodorosCompleted,
        settings: state.settings,
        focusStartTime: state.focusStartTime,
        runEndsAt: state.runEndsAt,
      }),
    },
  ),
)
